/* El tiempo de cada día del viaje (26a), de Open-Meteo (sin clave). Llega hasta 16 días vista, así que
   se va completando según se acerca el viaje. Se guarda en el móvil: sin red, el último que llegó. */
import { useEffect, useSyncExternalStore } from 'react';
import type { CiudadId, Dia, Viaje } from './tipos.ts';

interface DiaTiempo { codigo: number; max: number; min: number; lluvia: number }
interface Guardado { pedido: number; dias: Record<string, Record<string, DiaTiempo>>; horas: Record<string, Record<string, [number, number]>> }

const CLAVE = 'tabiji.tiempo.v1';
const CADA = 3 * 3600_000;               // se vuelve a pedir cada 3 horas como mucho
let estado: Guardado = (() => { try { return JSON.parse(localStorage.getItem(CLAVE) ?? 'null') ?? { pedido: 0, dias: {}, horas: {} }; } catch { return { pedido: 0, dias: {}, horas: {} }; } })();
const oyentes = new Set<() => void>();

/** Ciudad cuya previsión vale para ese día (los de tránsito: Osaka, donde se llega y de donde se sale). */
export const ciudadTiempo = (d: Dia): Exclude<CiudadId, 'transito'> => (d.ciudad === 'transito' ? 'osaka' : d.ciudad);

let pidiendo = false;
export async function pedirTiempo(v: Viaje) {
  if (pidiendo || !navigator.onLine || Date.now() - estado.pedido < CADA) return;
  pidiendo = true;
  try {
    const ciudades = v.ciudades.filter(c => c.lat != null && c.id !== 'transito');
    const q = new URLSearchParams({
      latitude: ciudades.map(c => c.lat).join(','), longitude: ciudades.map(c => c.lon).join(','),
      daily: 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max',
      hourly: 'precipitation_probability,weather_code', timezone: v.viaje.zonaHoraria, forecast_days: '16',
    });
    const r = await fetch(`https://api.open-meteo.com/v1/forecast?${q}`);
    if (!r.ok) return;
    const datos = await r.json();
    const lista = Array.isArray(datos) ? datos : [datos];
    const nuevo: Guardado = { pedido: Date.now(), dias: { ...estado.dias }, horas: { ...estado.horas } };
    lista.forEach((x, i) => {
      const id = ciudades[i].id;
      const dias: Record<string, DiaTiempo> = { ...nuevo.dias[id] };
      x.daily.time.forEach((t: string, k: number) => {
        dias[t] = { codigo: x.daily.weather_code[k], max: x.daily.temperature_2m_max[k], min: x.daily.temperature_2m_min[k], lluvia: x.daily.precipitation_probability_max[k] };
      });
      const horas: Record<string, [number, number]> = { ...nuevo.horas[id] };
      x.hourly.time.forEach((t: string, k: number) => { horas[t] = [x.hourly.precipitation_probability[k], x.hourly.weather_code[k]]; });
      nuevo.dias[id] = dias;
      nuevo.horas[id] = horas;
    });
    estado = nuevo;
    try { localStorage.setItem(CLAVE, JSON.stringify(estado)); } catch { /* sin sitio */ }
    oyentes.forEach(f => f());
  } catch { /* sin red: se queda lo último */ } finally { pidiendo = false; }
}

export function useTiempo(v: Viaje) {
  useEffect(() => { void pedirTiempo(v); }, [v]);
  return useSyncExternalStore(f => { oyentes.add(f); return () => oyentes.delete(f); }, () => estado);
}

export const tiempoDe = (g: Guardado, d: Dia): DiaTiempo | undefined => g.dias[ciudadTiempo(d)]?.[d.fecha];

/** Cuánto llueve a esa hora, de 0 (nada) a 1 (tormenta): llovizna se ve poco y un chaparrón mucho.
    Solo cuenta si la probabilidad pasa del 60 %. Es decorado: el texto está en la hoja del día. */
export function lluvia(g: Guardado, d: Dia, min: number): number {
  const h = Math.floor((((min % 1440) + 1440) % 1440) / 60);
  // Pasada la medianoche, la hora es del día siguiente en el calendario
  const fecha = min >= 1440 ? new Date(Date.parse(`${d.fecha}T12:00:00Z`) + 864e5).toISOString().slice(0, 10) : d.fecha;
  const x = g.horas[ciudadTiempo(d)]?.[`${fecha}T${String(h).padStart(2, '0')}:00`];
  if (!x || x[0] < 60) return 0;
  const c = x[1];
  const fuerza = c >= 95 ? 1 : c === 82 ? 1 : c === 81 ? 0.75 : c === 80 ? 0.5 : c >= 65 && c <= 67 ? 0.9 : c === 63 ? 0.7 : c === 61 ? 0.45 : c >= 51 && c <= 57 ? 0.3 : 0;
  return fuerza * (0.6 + 0.4 * (x[0] - 60) / 40);
}

/** Código WMO → kanji y texto. */
export function cielo(codigo: number): [string, string] {
  if (codigo === 0) return ['晴', 'Despejado'];
  if (codigo <= 2) return ['晴', 'Sol y nubes'];
  if (codigo === 3) return ['曇', 'Nublado'];
  if (codigo <= 48) return ['霧', 'Niebla'];
  if (codigo <= 57) return ['雨', 'Llovizna'];
  if (codigo <= 67) return ['雨', 'Lluvia'];
  if (codigo <= 77) return ['雪', 'Nieve'];
  if (codigo <= 82) return ['雨', 'Chubascos'];
  if (codigo <= 86) return ['雪', 'Nieve'];
  return ['雷', 'Tormenta'];
}
